import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  User,
  Bell,
  Building,
  Users,
  Film,
  ClipboardList,
  ReceiptText,
  QrCode,
  Building2,
  Globe,
  Settings,
  Truck,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Database,
  Zap,
  Cpu,
  Bot,
  Share2,
  Smartphone,
  FolderOpen,
  Siren,
  GraduationCap,
  ShieldCheck,
  Map as MapIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CapabilityName } from '@/contexts/RBACContext';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useLabels } from '@/hooks/useLabels';
import { useUserRole } from '@/hooks/useUserRole';
import { useFilters } from '@/contexts/FilterContext';
import { currentSupabaseEnv } from '@/lib/supabase';
import { getAdminUsers, getAdminGroups, getAdminUnits, getAdminCompanys } from '@/lib/adminApi';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  // T-068 P3: gate this item on a server capability flag (e.g.
  // report_subscriptions_admin) instead of a permission string. When set, the
  // item is visible iff the capability is true — regardless of permissions.
  capability?: CapabilityName;
  badge?: string;
  // True for items whose badge is an async count (Users/Groups/Units/Companies).
  // Their badge slot is reserved from first paint so it doesn't reflow when the
  // count arrives (CLS fix).
  hasCount?: boolean;
  children?: NavItem[]; // Nested links support
}

interface NavSection {
  title: string;
  items: NavItem[];
  permission?: string;
  // T-068 P3: a section is visible if its permission passes OR this capability is true
  // (so a company-wide admin sees "Alerts & Reports" for the inventory entry only).
  // A list means "visible if ANY of these is true" — a section whose items have
  // different capability gates must name them all, or the section hides an item
  // its holder is entitled to.
  capability?: CapabilityName | CapabilityName[];
}

const navSections: NavSection[] = [
  {
    title: 'General',
    items: [
      {
        name: 'Overview',
        path: '/settings',
        icon: LayoutDashboard,
      },
      {
        name: 'My Profile',
        path: '/settings/profile',
        icon: User,
      },
      {
        name: 'Notifications',
        path: '/settings/notifications',
        icon: Bell,
        badge: '3',
      },
      // Messages and Invoices - TBD, hidden for now
      // {
      //   name: 'Messages',
      //   path: '/settings/messages',
      //   icon: MessageSquare,
      //   badge: 'New',
      // },
      // {
      //   name: 'Invoices',
      //   path: '/settings/invoices',
      //   icon: FileText,
      // },
    ],
  },
  {
    title: 'Organization',
    items: [
      {
        name: 'Company Profile',
        path: '/settings/company',
        icon: Building,
        permission: 'company.settings',
      },
      {
        name: 'Training',
        path: '/settings/training',
        icon: GraduationCap,
        permission: 'company.settings',
      },
      {
        name: 'Users',
        path: '/settings/users',
        icon: Users,
        permission: 'users.read',
      },
      {
        name: 'Groups', // Will be dynamically replaced based on labels
        path: '/settings/groups',
        icon: Film,
        permission: 'groups.read',
      },
      {
        name: 'Units',
        path: '/settings/units',
        icon: Truck,
        permission: 'units.read',
      },
      {
        name: 'Audit Logs',
        path: '/settings/audit',
        icon: ClipboardList,
        permission: 'audit.read',
      },
      // Check-Ins moved to /records/check-ins
      {
        name: 'QR Codes',
        path: '/settings/qr-codes',
        icon: QrCode,
        permission: 'platform.all',
      },
    ],
  },
  {
    title: 'Platform',
    permission: 'platform.all',
    items: [
      {
        name: 'All Companies',
        path: '/settings/companies',
        icon: Building2,
        permission: 'companies.read',
      },
      {
        name: 'Global Audit',
        path: '/settings/global-audit',
        icon: Globe,
        permission: 'audit.global',
      },
      {
        name: 'Metadata',
        path: '/settings/filters',
        icon: Layers,
        permission: 'filters.manage',
      },
      {
        // T-071: moved from Records → Floorplans (model drawings damage pins sit on)
        name: 'Floorplans',
        path: '/settings/floorplans',
        icon: MapIcon,
        permission: 'platform.all',
      },
      {
        name: 'Electric Rates',
        path: '/settings/rates',
        icon: Zap,
        permission: 'rates.manage',
      },
      {
        name: 'Document Types',
        path: '/settings/document-types',
        icon: FolderOpen,
        permission: 'platform.all',
      },
      {
        name: 'Document Shares',
        path: '/settings/document-shares',
        icon: Share2,
        permission: 'platform.all',
      },
      {
        // T-045: what a client's certificate of insurance must show. Superadmin (server-enforced).
        name: 'Client Portal',
        path: '/settings/client-portal',
        icon: ShieldCheck,
        permission: 'platform.all',
      },
      {
        name: 'System Devices',
        path: '/settings/devices',
        icon: Cpu,
        permission: 'platform.all',
      },
      {
        name: 'System Preferences',
        path: '/settings/config',
        icon: Settings,
        permission: 'config.manage',
      },
      {
        // APP-AI-1: provider order + health. Superadmin only (server-enforced).
        name: 'AI',
        path: '/settings/ai',
        icon: Bot,
        permission: 'platform.all',
      },
      // QR Codes entry intentionally removed from Platform section (was a
      // duplicate of the Account-section entry). All QR management now
      // lives under /settings/pocket > Fleet.
      // T-046: read-only ops surface for the Pocket QR + Universal-Link
      // pipeline. Future T-047/T-048/T-049 add tabs to the same page.
      {
        name: 'Pocket',
        path: '/settings/pocket',
        icon: Smartphone,
        permission: 'platform.all',
      },
    ],
  },
  {
    // Own top-level section — alert rules + recipients (un-nested from System
    // Preferences → a tab, per Eric) plus the scheduled-reports dispatch switch.
    // Superadmin-only, matching where they lived.
    title: 'Alerts & Reports',
    permission: 'platform.all',
    // T-068 P3: also visible to a company-wide admin who holds the inventory
    // capability — they see ONLY the "Report subscriptions" item below, not the
    // superadmin-only "Configuration" (email-route toggles).
    capability: ['report_subscriptions_admin', 'unbilled_deployments_view'],
    items: [
      {
        name: 'Configuration',
        path: '/settings/alerts',
        icon: Siren,
        permission: 'platform.all',
      },
      {
        name: 'Report subscriptions',
        path: '/settings/report-subscriptions',
        icon: ClipboardList,
        capability: 'report_subscriptions_admin',
      },
      {
        // Internal staff + superadmin only (see UserCapabilities).
        name: 'Unbilled deployments',
        path: '/settings/unbilled-deployments',
        icon: ReceiptText,
        capability: 'unbilled_deployments_view',
      },
    ],
  },
];

interface SettingsSidebarProps {
  permissions?: string[];
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

export function SettingsSidebar({ permissions: propPermissions = [], collapsed, onToggle }: SettingsSidebarProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, is_superadmin, permissions: contextPermissions, capabilities, loading } = useSettingsContext();
  const { getLabel } = useLabels();
  const { isGroupAdmin, role } = useUserRole();
  const { selectedGroupIds, selectedModels, selectedStatuses } = useFilters();

  const permissions = contextPermissions.length > 0 ? contextPermissions : propPermissions;
  
  // State for dynamic counts
  const [counts, setCounts] = useState({
    users: 0,
    groups: 0,
    units: 0,
    companies: 0,
  });
  // Whether the async count fetch has settled — until then, count badges show a
  // fixed-size skeleton so the nav doesn't reflow when the numbers land (CLS fix).
  const [countsReady, setCountsReady] = useState(false);
  
  // Fetch counts for sidebar badges - filters applied for instant updates
  useEffect(() => {
    if (loading || !user) return;

    async function fetchCounts() {
      try {
        const [usersRes, groupsRes, unitsRes, companiesRes] = await Promise.allSettled([
          // Users and groups: only filter by groups (not by status/models - those only apply to units)
          getAdminUsers({ groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined }),
          getAdminGroups({ groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined }),
          // Units: filter by groups, models, AND statuses (units have all these attributes)
          getAdminUnits({
            groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
            models: selectedModels.length > 0 ? selectedModels : undefined,
            statuses: selectedStatuses.length > 0 && selectedStatuses.length < 5 ? selectedStatuses : undefined
          }),
          // Companies: no filters (top-level entity)
          getAdminCompanys(),
        ]);

        const newCounts = {
          users: usersRes.status === 'fulfilled' ? usersRes.value.users?.length || 0 : 0,
          groups: groupsRes.status === 'fulfilled' ? groupsRes.value.groups?.length || 0 : 0,
          units: unitsRes.status === 'fulfilled' ? unitsRes.value.units?.length || 0 : 0,
          companies: companiesRes.status === 'fulfilled' ? companiesRes.value.companies?.length || 0 : 0,
        };

        console.log('[SettingsSidebar] Fetched filtered counts:', newCounts, 'Filters:', {
          groupIds: selectedGroupIds,
          models: selectedModels,
          statuses: selectedStatuses,
          note: 'Status/model filters only apply to units'
        });

        setCounts(newCounts);
      } catch (error) {
        console.error('[SettingsSidebar] Failed to fetch counts:', error);
      } finally {
        // Settle the badge slots either way (a failed fetch shouldn't leave a
        // permanent skeleton — it collapses to the reserved empty slot).
        setCountsReady(true);
      }
    }

    fetchCounts();
  }, [loading, user, selectedGroupIds, selectedModels, selectedStatuses]);
  
  // Get dynamic labels
  const groupLabel = getLabel('group');
  const unitLabel = getLabel('unit');
  
  // Helper function to properly pluralize labels
  const pluralize = (word: string): string => {
    // If word is empty, return as-is
    if (!word) return word;
    
    // Capitalize first letter
    const capitalized = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    
    // Handle common plural forms
    if (capitalized.endsWith('y') && !'aeiou'.includes(capitalized.charAt(capitalized.length - 2).toLowerCase())) {
      // e.g., "Company" -> "Companies", "Category" -> "Categories"
      return capitalized.slice(0, -1) + 'ies';
    } else if (capitalized.endsWith('s') || capitalized.endsWith('sh') || capitalized.endsWith('ch') || capitalized.endsWith('x') || capitalized.endsWith('z')) {
      // e.g., "Class" -> "Classes", "Branch" -> "Branches"
      return capitalized + 'es';
    } else {
      // Default: just add 's'
      // e.g., "Production" -> "Productions", "Team" -> "Teams"
      return capitalized + 's';
    }
  };
  
  // Platform features that should be hidden for group_admins
  // Messages and Invoices are TBD, hidden for everyone for now
  const platformFeatures = ['Notifications'];

  // Create nav sections with dynamic labels and filtered items
  // Create nav sections with dynamic labels and filtered items
  const navSectionsWithLabels: NavSection[] = navSections
    .filter(section => {
      // Hide PLATFORM section for non-superadmins
      if (section.title === 'Platform') {
        return is_superadmin;
      }
      return true;
    })
    .map(section => ({
      ...section,
      items: section.items
        .filter(item => {
          // Hide platform features for group_admins
          if (isGroupAdmin && platformFeatures.includes(item.name)) {
            return false;
          }
          return true;
        })
        .map(item => {
          // Replace "Groups" with the dynamic plural label, preserve children (Sites)
          if (item.name === 'Groups') {
            const badge = counts.groups > 0 ? counts.groups.toString() : undefined;
            console.log('[SettingsSidebar] Groups badge:', badge, 'count:', counts.groups);
            return {
              ...item,
              name: pluralize(groupLabel),
              badge,
              hasCount: true,
              children: item.children // Preserve Sites child link
            };
          }
          
          // Add dynamic count badges for organization pages
          if (item.name === 'Users') {
            const badge = counts.users > 0 ? counts.users.toString() : undefined;
            return { ...item, badge, hasCount: true };
          }
          
          if (item.name === 'Units') {
            const badge = counts.units > 0 ? counts.units.toString() : undefined;
            console.log('[SettingsSidebar] Units badge:', badge, 'count:', counts.units);
            return {
              ...item,
              name: unitLabel, // Use singular form (e.g., "Fleet")
              badge,
              hasCount: true
            };
          }

          // All Companies - add badge for superadmins
          if (item.name === 'All Companies') {
            const badge = counts.companies > 0 ? counts.companies.toString() : undefined;
            return { ...item, badge, hasCount: true };
          }

          return item;
        })
    }));

  // Helper to check permissions
  const hasPermission = (required?: string) => {
    if (is_superadmin) return true;
    if (!required) return true;
    return permissions.includes(required) || permissions.includes('platform.all');
  };

  // T-068 P3: an item/section may gate on a server capability instead of (or in
  // addition to) a permission string. A capability item is visible iff the flag
  // is true — a permission item follows hasPermission.
  const hasCapability = (cap?: CapabilityName | CapabilityName[]) => {
    if (!cap) return false;
    const list = Array.isArray(cap) ? cap : [cap];
    return list.some((c) => capabilities?.[c] === true);
  };
  const itemVisible = (item: NavItem) => (item.capability ? hasCapability(item.capability) : hasPermission(item.permission));
  const sectionVisible = (section: NavSection) =>
    (!section.permission || hasPermission(section.permission)) || hasCapability(section.capability);

  // Helper to construct path with preserved query params
  const getPathWithParams = (path: string) => {
    const currentParams = searchParams.toString();
    if (!currentParams) return path;
    return `${path}${path.includes('?') ? '&' : '?'}${currentParams}`;
  };


  const NavItemContent = ({ item, isActive, collapsed, countsReady }: { item: NavItem, isActive: boolean, collapsed: boolean, countsReady: boolean }) => {
    const Icon = item.icon;
    // For collapsible items
    const hasChildren = item.children && item.children.length > 0;

    // Check if any child is active to auto-expand
    const isChildActive = hasChildren && item.children?.some(child => location.pathname + location.search === child.path);

    // Initialize isOpen based on whether a child is active (prevents re-render loop)
    const [isOpen, setIsOpen] = useState(isChildActive || false);

    // Sync state when child becomes active (e.g., navigation)
    useEffect(() => {
      if (isChildActive && !isOpen) {
        setIsOpen(true);
      }
    }, [isChildActive, isOpen]);

    const handleToggle = (e: React.MouseEvent) => {
      if (hasChildren) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    return (
      <>
        <Link
          to={hasChildren ? '#' : getPathWithParams(item.path)}
          onClick={hasChildren ? handleToggle : undefined}
          className={cn(
            'group flex items-center rounded-lg text-sm font-medium transition-all duration-200 select-none',
            collapsed ? 'justify-center px-2 py-2' : 'justify-between px-3 py-2',
            isActive && !hasChildren
              ? 'bg-[#8FB560] bg-opacity-10 text-[#8FB560]'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          )}
        >
          <div className={cn("flex items-center gap-3", collapsed && "gap-0")}>
            <Icon className={cn("w-5 h-5 flex-shrink-0", (isActive || isChildActive) ? "text-[#8FB560]" : "text-muted-foreground group-hover:text-foreground")} />
            {!collapsed && <span className="whitespace-nowrap">{item.name}</span>}
          </div>
          
          {!collapsed && (
            <div className="flex items-center gap-2">
              {item.hasCount ? (
                // Count badge: the slot is a FIXED size from first paint so the row
                // never reflows when the async count lands (CLS fix). Skeleton while
                // pending; the number once ready; a same-size empty slot when the
                // count is 0 (so it doesn't pop out either).
                !countsReady ? (
                  <div className="h-[22px] w-8 rounded-full bg-muted/50 animate-pulse" aria-hidden="true" />
                ) : item.badge ? (
                  <div className={cn(
                    "h-[22px] min-w-8 px-2 flex items-center justify-center rounded-full border transition-colors",
                    isActive ? "bg-[#8FB560]/20 border-[#8FB560]/50" : "bg-muted/50 border-border/50 opacity-60"
                  )}>
                    <span className={cn(
                      "text-xs font-medium tabular-nums",
                      isActive ? "text-[#8FB560]" : "text-muted-foreground"
                    )}>
                      {item.badge}
                    </span>
                  </div>
                ) : (
                  <div className="h-[22px] w-8" aria-hidden="true" />
                )
              ) : item.badge ? (
                <div className={cn(
                  "px-2 py-0.5 rounded-full border transition-colors",
                  isActive ? "bg-[#8FB560]/20 border-[#8FB560]/50" : "bg-muted/50 border-border/50 opacity-60"
                )}>
                  <span className={cn(
                    "text-xs font-medium tabular-nums",
                    isActive ? "text-[#8FB560]" : "text-muted-foreground"
                  )}>
                    {item.badge}
                  </span>
                </div>
              ) : null}
              {hasChildren && (
                <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isOpen ? "transform rotate-180" : "")} />
              )}
            </div>
          )}
          
          {collapsed && item.badge && (
            <div className="absolute top-1 right-1 w-2 h-2 bg-[#8FB560] rounded-full border border-card" />
          )}
        </Link>

        {/* Render Children */}
        {!collapsed && hasChildren && isOpen && (
          <div className="ml-4 space-y-1 border-l border-border pl-2 my-1 animate-in slide-in-from-top-2 duration-200">
            {item.children!.map((child) => {
              const isChildLinkActive = location.pathname + location.search === child.path;
              const ChildIcon = child.icon;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isChildLinkActive
                      ? 'text-[#8FB560] bg-[#8FB560]/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  {ChildIcon && <ChildIcon className="w-4 h-4" />}
                  <span>{child.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // Calculate if sidebar should be expanded
  // Expanded if NOT collapsed manually OR if hover state is active (only when collapsed)
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = !collapsed || isHovered;

  return (
    <aside 
      className={cn(
        "absolute top-0 left-0 bottom-0 bg-card border-r border-border flex flex-col transition-all duration-300 z-50 shadow-xl",
        !collapsed 
          ? "w-[240px] lg:w-[360px]" 
          : (isHovered ? "w-[240px]" : "w-[60px]") // Removed overflow-hidden so shadow isn't clipped, but button logic handles overflow
      )}
      style={{ overflow: 'visible' }}
      onMouseEnter={() => {
        if (collapsed) setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {/* Toggle Button - Matches Frontend Sidebar Style */}
      <button
        onClick={() => onToggle(!collapsed)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-border hover:bg-accent rounded-full flex items-center justify-center transition-colors shadow-md"
      >
        {collapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </button>

      <div className="p-4 space-y-8 flex-1 overflow-y-auto no-scrollbar">
        {loading ? (
          // Loading skeleton - prevent flicker by showing placeholders
          <div className="space-y-8 animate-pulse">
            {[1, 2].map((section) => (
              <div key={section} className="space-y-1">
                {isExpanded && (
                  <div className="h-3 bg-muted rounded w-24 mb-3 mx-3" />
                )}
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className={cn(
                      "rounded-lg",
                      collapsed ? "h-9 w-9 mx-auto bg-muted" : "h-9 bg-muted mx-3"
                    )}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          // Render navigation only after loading completes
          navSectionsWithLabels.map((section, idx) => {
            // Skip section if user doesn't have permission
            if (!sectionVisible(section)) {
              return null;
            }

            // Filter items in section
            const visibleItems = section.items.filter(itemVisible);

            if (visibleItems.length === 0) return null;

            return (
              <div key={idx} className={cn("space-y-1", !isExpanded && "text-center")}>
                {isExpanded && (
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-3 whitespace-nowrap">
                    {section.title}
                  </h3>
                )}
                <nav className="space-y-1">
                  {visibleItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <NavItemContent key={item.path} item={item} isActive={isActive} collapsed={!isExpanded} countsReady={countsReady} />
                    );
                  })}
                </nav>
              </div>
            );
          })
        )}
      </div>
      
      {/* Debug Info - Temporary */}
      {isExpanded && !loading && (
        <div className="p-4 border-t border-border text-[10px] text-muted-foreground font-mono bg-card/50 whitespace-nowrap overflow-hidden">
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3" />
              DB:
            </span>
            <span className={currentSupabaseEnv === 'demo' ? "text-amber-400" : "text-blue-400"}>
              {currentSupabaseEnv === 'demo' ? 'DEMO' : 'PRODUCTION'}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span>Role:</span>
            <span className={
              role === 'superadmin' ? "text-red-400" :
              role === 'admin' ? "text-purple-400" :
              role === 'company_admin' ? "text-green-400" :
              role === 'group_admin' ? "text-blue-400" :
              "text-muted-foreground"
            }>
              {role === 'superadmin' ? 'Superadmin' :
               role === 'admin' ? 'Admin' :
               role === 'company_admin' ? 'Company Admin' :
               role === 'group_admin' ? 'Group Admin' :
               'User'}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span>Perms:</span>
            <span>{permissions.length}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span>Internal:</span>
            <span className={user?.is_internal ? "text-green-400" : "text-yellow-400"}>
              {user?.is_internal ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      )}
    </aside>
  );
}